// src/pages/FAQs.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import FAQItem from '../components/FAQItem';
import GradientButton from '../components/GradientButton';

const faqList = [
  // --- Section 1: Simple Reassurance ---
  {
    question: 'Is LuckyLogic.ai a gambling site?',
    answer: 'No. LuckyLogic.ai is a subscription-based information and education service. We don’t sell lottery tickets or run lottery games. We simply send you AI-generated number sets — what you do with them is up to you.'
  },
  {
    question: 'Can I buy lottery tickets on LuckyLogic.ai?',
    answer: 'No. Tickets must be purchased through your local authorized lottery retailer.'
  },
  {
    question: 'Is LuckyLogic.ai partnered with Powerball or Mega Millions?',
    answer: 'No. LuckyLogic.ai is an independent subscription service. We’re not affiliated with, endorsed by, or sponsored by Powerball®, Mega Millions®, or any lottery organization. Those names are trademarks of their owners — we only mention them to show which games we support. LuckyLogic.ai is strictly an informational and educational platform.'
  },
  {
    question: 'Will LuckyLogic.ai improve my odds of winning?',
    answer: 'No. Nothing can change the odds of lottery games. We use AI to analyze past results and trends to generate number sets, but lotteries are still games of chance and winning numbers can’t be predicted.'
  },

  // --- Section 2: How It Works ---
  {
    question: 'What does LuckyLogic.ai actually do?',
    answer: 'We study historical lottery data and patterns, then generate unique number sets for our subscribers. You’ll get your numbers by text and/or email before each draw.'
  },
  {
    question: 'How many number sets do I get for each lottery draw?',
    answer: 'Every member is guaranteed five (5) number sets for each lottery they subscribe to. Each set includes five white ball numbers and one red ball (Powerball or Mega Ball). Depending on availability, members may get up to ten (10) sets. Availability depends on the total number of sets we generate for a draw and the total number of active subscribers. Everyone gets at least five; anything extra is a bonus.'
  },
  {
    question: 'How often do I receive numbers?',
    answer: 'You can customize what games and days you receive number sets.  Powerball draws are (Mon/Wed/Sat) and Mega Millions draws are (Tues/Fri).  You’ll get your number sets at least 24 hours before each draw.'
  },
  {
    question: 'How are the numbers created?',
    answer: 'We use an AI-driven logic engine that looks at past draw data and applies custom rules to generate unique number sets.'
  },
  {
    question: 'Do other people get the same numbers as me?',
    answer: 'No. Each subscriber gets a unique set. We don’t issue duplicate number combinations.'
  },
  {
    question: 'Does LuckyLogic.ai track draw results?',
    answer: 'Yes. We automatically pull official results and use them to refine our number logic for future draws.'
  },
  {
    question: 'Can I choose which lottery game I want?',
    answer: 'Yes. You can subscribe to Powerball, Mega Millions, or both, and manage your choices in your profile settings.'
  },
  {
    question: 'How do I know my subscription is active?',
    answer: "When you sign up, your first number sets are sent immediately by text and/or email even if your jackpot thresholds aren't met. After that, you'll get fresh sets at least 24 hours before each draw in line with your plan and preferences."
  },
  {
    question: 'What if my numbers don’t arrive?',
    answer: 'Contact support through the Contact link at the bottom of our site pages and we’ll resend your numbers and fix the issue.'
  },
  {
    question: 'Do you keep records of the numbers I’ve received?',
    answer: 'Yes. We save both the full pool we generate and the individual number sets issued to each member.'
  },

  // --- Section 3: Costs & Accounts ---
  {
    question: 'How much does LuckyLogic.ai cost?',
    answer: 'Two options:\n- Jackpot Chaser is $14.99/annually, non-refundable\n- Weekly Player is $34.99/annually, non-refundable'
  },
  {
    question: 'Can I cancel my subscription?',
    answer: 'No. Jackpot Chaser and Weekly Player plans are annual plans, prepaid, and non-refundable. To cancel, go to your profile and turn off Auto-Renew or close your account.'
  },
  {
    question: 'Does my account auto-renew?',
    answer: 'Yes. All plans renew automatically by default. You can manage Auto-Renew anytime in your profile settings. If it’s left on, renewals are final and non-refundable.'
  },

  // --- Section 4: Big Wins & Obligations ---
  {
    question: 'What happens if I win using LuckyLogic.ai numbers?',
    answer: 'Congrats! Do what you want with your prize. For jackpots over $50 million, members agree to pay 2.5% of their gross cash winnings to LottoLogic LLC (the operator of LuckyLogic.ai). See the Terms and Conditions for full details.'
  },

  // --- Section 5: Extras ---
  {
    question: 'Do I need to download an app?',
    answer: 'No. LuckyLogic.ai works in your browser and on mobile. No app required.'
  },
  {
    question: 'Can I contact someone if I have questions?',
    answer: 'Yes. Use the Contact link at the bottom of any page. Our AI assistant handles most questions, and we’ll step in if you need more help.'
  },
  {
    question: 'Do you share or sell my personal data?',
    answer: 'No. We don’t sell or share your data. See our Privacy Policy (linked at the bottom of each page) for details.'
  },
  {
    question: 'Can I change how I receive my numbers (email/text)?',
    answer: 'Yes. Update your communication preferences anytime in your profile settings.'
  }
];

const FAQs = () => {
  const navigate = useNavigate();

  return (
    <div style={{ 
      fontFamily: "'Open Sans', sans-serif",
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #14B1E7 0%, #0EA5E9 100%)',
        borderRadius: '16px',
        padding: '2rem 1rem',
        textAlign: 'center',
        margin: '1rem',
        boxShadow: '0 8px 32px rgba(20, 177, 231, 0.3)'
      }}>
        <h1 style={{
          margin: 0,
          color: '#FFF',
          fontWeight: 'bold',
          fontSize: 'clamp(2rem, 8vw, 3rem)',
          textShadow: '2px 2px rgba(0,0,0,0.2)',
          lineHeight: '1.2'
        }}>
          Frequently Asked Questions
        </h1>
        <p style={{
          color: '#FFF',
          fontSize: 'clamp(1rem, 4vw, 1.25rem)',
          margin: '1rem 0 0 0',
          opacity: 0.95
        }}>
          Everything you want and need to know about LuckyLogic.ai
        </p>
      </div>

      {/* FAQ Container */}
      <div style={{
        padding: '2rem 1rem',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        {/* FAQ List with Border */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e5e7eb',
          marginBottom: '2rem'
        }}>
          {faqList.map((faq, index) => (
            <div key={index} style={{
              borderBottom: index < faqList.length - 1 ? '1px solid #f3f4f6' : 'none',
              paddingBottom: index < faqList.length - 1 ? '1.5rem' : '0',
              marginBottom: index < faqList.length - 1 ? '1.5rem' : '0'
            }}>
              <FAQItem question={faq.question} answer={faq.answer} />
            </div>
          ))}
        </div>

        {/* Back to Homepage Button */}
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <GradientButton
            variant="blue"
            onClick={() => navigate('/')}
            style={{ maxWidth: '90%' }}
          >
            ← Back to Home
          </GradientButton>
        </div>
      </div>
    </div>
  );
};

export default FAQs;
